// Copyright 2025, University of Maryland, All Rights Reserved
import { useEffect } from 'react';
import { Settings } from 'lucide-react';
import { groupPromptsByAnalysisType } from '../utils/prompts';
import type { ConfigurationLevel } from '../types/config';
import type { PromptWithAnalysisType } from '../types/prompt';

interface PromptsListProps {
  level: ConfigurationLevel;
  selectedItem: PromptWithAnalysisType | null;
  onSelectItem: (item: string) => void;
  prompts?: string[];
  isLoading?: boolean;
}

export function PromptsList({
  level,
  selectedItem,
  onSelectItem,
  prompts = [],
  isLoading = false,
}: PromptsListProps) {

  // Auto-select the first prompt only when prompts first load
  useEffect(() => {
    if (prompts && prompts.length > 0 && !selectedItem) {
      const groupedPrompts = groupPromptsByAnalysisType(prompts);
      const sortedTypes = Object.keys(groupedPrompts).sort();
      if (sortedTypes.length > 0 && groupedPrompts[sortedTypes[0]].length > 0) {
        const firstPrompt = `${sortedTypes[0]}/${groupedPrompts[sortedTypes[0]][0]}`;
        onSelectItem(firstPrompt);
      }
    }
  }, [prompts?.length]); // Only depend on prompts length changing

  const groupedPrompts = groupPromptsByAnalysisType(prompts);
  const sortedAnalysisTypes = Object.keys(groupedPrompts).sort();

  return (
    <div className="lg:col-span-1 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-lg text-gray-800">💬 Prompts</h3>
      </div>

      <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
        {isLoading ? (
          <div className="text-center py-8 text-gray-600">Loading prompts...</div>
        ) : (
          <div className="space-y-4">
            {/* Grouped Prompts Display */}
            {sortedAnalysisTypes.map((type) => (
                <div key={type} className="space-y-2">
                  <div className="flex items-center justify-between px-2 py-1 bg-gray-300 rounded">
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                      {type}
                    </h4>
                    <span className="text-xs text-gray-700">
                      {groupedPrompts[type].length} {groupedPrompts[type].length === 1 ? 'prompt' : 'prompts'}
                    </span>
                  </div>
                  {groupedPrompts[type].map((promptName) => {
                    const fullName = `${type}/${promptName}`;
                    // Check if this prompt is selected by comparing name and analysis type
                    const isSelected = selectedItem?.name === promptName &&
                      selectedItem?.analysisType === type;

                    return (
                      <button
                        key={fullName}
                        onClick={() => onSelectItem(fullName)}
                        className={`w-full text-left px-3 py-2 rounded-md transition-colors border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isSelected
                            ? 'bg-blue-100 text-blue-900 border-blue-500 font-medium'
                            : 'bg-white hover:bg-gray-50 border-gray-300 text-gray-900'
                        }`}
                        aria-pressed={isSelected}
                        type="button"
                      >
                        <div className="font-medium">{promptName}</div>
                        <div className={`text-xs ${isSelected ? 'text-blue-800' : 'text-gray-600'}`}>
                          <span className={isSelected ? 'text-blue-900' : 'text-blue-700'}>{type}</span> • Prompt
                        </div>
                      </button>
                    );
                  })}
                </div>
              ))}

            {sortedAnalysisTypes.length === 0 && (
              <div className="text-center py-8 text-gray-600">
                <Settings className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No prompts found</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
